(() => {
  const OPEN_DURATION = 900;
  const CLOSE_DURATION = 600;
  const EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';

  const getPanel = (details) => details.querySelector('[data-description-accordion-panel]');

  const setIsOpenState = (details, isOpen) => {
    details.classList.toggle('is-open', isOpen);
    const summary = details.querySelector('[data-description-accordion-summary]');
    if (summary) summary.setAttribute('aria-expanded', String(isOpen));
  };

  const animateHeight = (panel, from, to, duration, done) => {
    panel.style.transition = 'none';
    panel.style.height = `${from}px`;
    panel.offsetHeight;
    panel.style.transition = `height ${duration}ms ${EASING}`;
    panel.style.height = `${to}px`;

    const onEnd = (e) => {
      if (e.propertyName !== 'height') return;
      panel.removeEventListener('transitionend', onEnd);
      panel.style.transition = '';
      done && done();
    };
    panel.addEventListener('transitionend', onEnd);
  };

  const openItem = (details) => {
    const panel = getPanel(details);
    if (!panel || details.dataset.animating === 'true') return;
    details.dataset.animating = 'true';
    details.setAttribute('open', '');
    setIsOpenState(details, true);
    panel.style.height = '0px';
    const target = panel.scrollHeight;
    requestAnimationFrame(() => {
      animateHeight(panel, 0, target, OPEN_DURATION, () => {
        panel.style.height = 'auto';
        details.dataset.animating = 'false';
      });
    });
  };

  const closeItem = (details, { targetItem = null } = {}) => {
    const panel = getPanel(details);
    if (!panel) return;

    if (!details.hasAttribute('open') && !details.classList.contains('is-open')) {
      panel.style.height = '0px';
      details.dataset.animating = 'false';
      return;
    }

    // ─── Smooth scroll anchor ─────────────────────────────────────────────
    // After the close animation, check if the target section's top has been
    // pushed out of view — if so, scroll it back into view smoothly.
    if (targetItem) {
      panel.addEventListener('transitionend', function onClose(e) {
        if (e.propertyName !== 'height') return;
        panel.removeEventListener('transitionend', onClose);

        const targetRect = targetItem.getBoundingClientRect();
        const isTopOutOfView = targetRect.top < 0;

        if (isTopOutOfView) {
          const HEADER_OFFSET = 50;
          const scrollTarget = targetRect.top + window.scrollY - HEADER_OFFSET;
          window.scrollTo({ top: scrollTarget, behavior: 'smooth' });
        }
      });
    }

    details.dataset.animating = 'true';
    setIsOpenState(details, false);
    const currentHeight = panel.getBoundingClientRect().height;
    panel.style.transition = 'none';
    panel.style.height = `${currentHeight}px`;
    panel.offsetHeight;
    requestAnimationFrame(() => {
      animateHeight(panel, currentHeight, 0, CLOSE_DURATION, () => {
        details.removeAttribute('open');
        panel.style.height = '0px';
        details.dataset.animating = 'false';
      });
    });
  };

  // ─── Core init — scoped to a root element ────────────────────────────────
  const initAccordions = (root) => {
    const accordions = root.querySelectorAll('[data-description-accordion]');
    if (!accordions.length) return;

    accordions.forEach((accordion) => {
      if (accordion.dataset.accordionInit === 'true') return;
      accordion.dataset.accordionInit = 'true';

      const items = Array.from(accordion.querySelectorAll('[data-description-accordion-item]'));
      if (!items.length) return;

      const initiallyOpen = items.find(d => d.hasAttribute('open')) || items[0];
      accordion._openItem = initiallyOpen;

      items.forEach((d) => {
        const panel = getPanel(d);
        if (!panel) return;
        const shouldBeOpen = d === initiallyOpen;

        if (shouldBeOpen) {
          d.setAttribute('open', '');
          setIsOpenState(d, true);
          const h = panel.scrollHeight;
          panel.style.height = `${h}px`;
          requestAnimationFrame(() => {
            if (d.classList.contains('is-open')) panel.style.height = 'auto';
          });
        } else {
          d.removeAttribute('open');
          setIsOpenState(d, false);
          panel.style.height = '0px';
          d.dataset.animating = 'false';
        }
      });

      accordion.addEventListener('click', (event) => {
        const summary = event.target.closest('[data-description-accordion-summary]');
        if (!summary || !accordion.contains(summary)) return;
        const details = summary.closest('[data-description-accordion-item]');
        if (!details || details.dataset.animating === 'true') return;
        event.preventDefault();

        const isOpen = details.classList.contains('is-open');

        if (isOpen) {
          closeItem(details);
          if (accordion._openItem === details) accordion._openItem = null;
          return;
        }

        // Pass the target item so closeItem can scroll it into view if needed
        items.forEach((d) => {
          if (d !== details && (d.classList.contains('is-open') || d.hasAttribute('open'))) {
            closeItem(d, { targetItem: details });
          }
        });

        openItem(details);
        accordion._openItem = details;
      });
    });
  };

  // ─── Initial page load ───────────────────────────────────────────────────
  document.addEventListener('DOMContentLoaded', () => initAccordions(document));

  // ─── Watch for Dawn modals being injected ────────────────────────────────
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        if (node.querySelector?.('[data-description-accordion]')) {
          setTimeout(() => initAccordions(node), 50);
        }
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
})();